import React, { useState, useEffect, useCallback } from 'react';
import {
  Sun, Moon, LogOut, Plus, Minus, Target, Wallet,
  TrendingUp, History, X, ArrowUpRight, ArrowDownLeft,
  LayoutDashboard, Settings, HelpCircle, Menu, ChevronRight,
  AlertTriangle, Calendar, Percent, Shield, Users, CheckCircle, Banknote, Clock,
  Copy, Check, QrCode, Loader, Zap, Activity
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

const formatCurrency = (value) =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (date) =>
  new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatTime = (date) =>
  new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function DashboardPage({ user, setUser, onLogout, theme, setTheme, setPage }) {
  const [history, setHistory] = useState([]);
  const [loans, setLoans] = useState([]);
  const [modal, setModal] = useState(null);
  const [amount, setAmount] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // PIX state
  const [pixData, setPixData] = useState(null); // QR code data after deposit request
  const [pixCopied, setPixCopied] = useState(false);
  const [pixPolling, setPixPolling] = useState(false);

  const balance = Number(user.balance || 0);
  const goal = Number(user.goal || 0);
  const progress = goal > 0 ? Math.min((balance / goal) * 100, 100) : 0;
  const remaining = Math.max(goal - balance, 0);
  const maxWithdraw = balance * 0.2;

  const loadData = useCallback(async () => {
    try {
      const [userData, txs, userLoans] = await Promise.all([api.me(), api.getTransactions(), api.getMyLoans()]);
      setUser(userData);
      setHistory(txs);
      setLoans(userLoans);
    } catch {}
  }, [setUser]);

  useEffect(() => { loadData(); }, [loadData]);

  const now = new Date();
  const isDailyMode = user.allow_daily_withdrawal;
  const hasWithdrawnRecently = isDailyMode
    ? history.some((h) => {
        if (h.type !== 'withdraw') return false;
        const d = new Date(h.created_at);
        return (now - d) < 24 * 60 * 60 * 1000;
      })
    : history.some((h) => {
        const d = new Date(h.created_at);
        return h.type === 'withdraw' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });

  const handleTransaction = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const type = modal;
      if (type === 'deposit') {
        // PIX deposit - generate QR code
        const result = await api.pixDeposit({ amount: parseFloat(amount), description: 'Depósito via PIX' });
        // Only show QR code screen if we actually got QR data or EMV code
        if (!result.qrCodeImage && !result.emvCode) {
          setError('Não foi possível gerar o QR Code PIX. Tente novamente em alguns instantes.');
          setLoading(false);
          return;
        }
        setPixData(result);
        setPixCopied(false);
        // Start polling for payment confirmation
        if (result.pixOperation?.id) {
          setPixPolling(true);
          const pollId = result.pixOperation.id;
          const interval = setInterval(async () => {
            try {
              const status = await api.pixStatus(pollId);
              if (status.status === 'paid') {
                clearInterval(interval);
                setPixPolling(false);
                setPixData(null);
                setAmount('');
                setModal(null);
                await loadData();
              }
            } catch {}
          }, 5000); // Poll every 5 seconds
          // Stop polling after 15 minutes
          setTimeout(() => { clearInterval(interval); setPixPolling(false); }, 15 * 60 * 1000);
        }
      } else {
        // PIX withdraw
        await api.pixWithdraw({ amount: parseFloat(amount), description: 'Saque via PIX' });
        setAmount('');
        setModal(null);
        await loadData();
      }
    } catch (err) {
      setError(err.error || err.message || 'Erro ao processar transação');
    } finally {
      setLoading(false);
    }
  };

  const copyEmvCode = () => {
    if (pixData?.emvCode) {
      navigator.clipboard.writeText(pixData.emvCode);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 2000);
    }
  };

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const isAdmin = user.role === 'admin' || user.role === 'supervisor';

  const withdrawError = (() => {
    if (hasWithdrawnRecently) return isDailyMode ? 'Você já realizou o saque nas últimas 24 horas' : 'Você já realizou o saque deste mês';
    const val = parseFloat(amount);
    if (val && val > maxWithdraw) return `Valor máximo permitido: ${formatCurrency(maxWithdraw)}`;
    if (val && val > balance) return 'Saldo insuficiente';
    return null;
  })();

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            {isAdmin && (
              <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
                <Users size={19} /><span>Admin</span>
              </a>
            )}
            {isAdmin && (
              <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
                <Zap size={19} /><span>Automacao IA</span>
              </a>
            )}
            {isAdmin && (
              <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
                <Activity size={19} /><span>Atividade</span>
              </a>
            )}
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Histórico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configurações</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main">
          <div className="page-header">
            <div>
              <p className="page-greeting">Olá, {(user.name || '').split(' ')[0]}</p>
              <h1 className="page-title">Quitação Planejada</h1>
            </div>
            {user.role === 'usuario' && user.status !== 'acordo_pago' && (
              <div className="page-actions">
                <button className="action-btn deposit" onClick={() => setModal('deposit')}>
                  <Plus size={17} /><span>Adicionar</span>
                </button>
                <button className="action-btn withdraw" onClick={() => setModal('withdraw')}>
                  <Minus size={17} /><span>Retirar</span>
                </button>
              </div>
            )}
          </div>

          {user.status === 'acordo_pago' && (
            <div className="acordo-pago-banner">
              <CheckCircle size={20} />
              <div>
                <strong>Acordo Pago!</strong>
                <p>Parabéns! Sua meta foi concluída e o acordo foi finalizado. Não é possível realizar novas transações.</p>
              </div>
            </div>
          )}

          <div className="balance-hero">
            <div className="hero-glow" />
            <div className="balance-hero-left">
              <span className="balance-hero-label">Saldo acumulado</span>
              <span className="balance-hero-value">{formatCurrency(balance)}</span>
              <div className="balance-hero-bar">
                <div className="balance-hero-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="balance-hero-meta">
                <span>{progress.toFixed(1)}% da meta</span>
                <span>Faltam {formatCurrency(remaining)}</span>
              </div>
            </div>
            <div className="balance-hero-right">
              <div className="hero-ring">
                <svg viewBox="0 0 120 120" className="ring-svg">
                  <circle cx="60" cy="60" r="52" className="ring-bg" />
                  <circle cx="60" cy="60" r="52" className="ring-fill"
                    strokeDasharray={`${progress * 3.267} ${326.7 - progress * 3.267}`}
                    strokeDashoffset="81.675" />
                </svg>
                <div className="ring-center">
                  <span className="ring-percent">{Math.round(progress)}%</span>
                  <span className="ring-label">concluído</span>
                </div>
              </div>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon blue"><Wallet size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Saldo atual</span>
                <span className="stat-value">{formatCurrency(balance)}</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="stat-card">
              <div className="stat-icon green"><Target size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Meta de quitação</span>
                <span className="stat-value">{formatCurrency(goal)}</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="stat-card">
              <div className="stat-icon orange"><TrendingUp size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Falta para a meta</span>
                <span className="stat-value">{formatCurrency(remaining)}</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
          </div>

          {user.role === 'usuario' && user.status !== 'acordo_pago' && (
            <div className="rules-banner">
              <Shield size={16} className="rules-icon" />
              <span>Saques limitados a <strong>1x por mês</strong> e até <strong>20% do saldo</strong> disponível.</span>
            </div>
          )}

          {/* Deposit Due Day Banner */}
          {user.deposit_due_day && user.status !== 'acordo_pago' && (() => {
            const dueDay = user.deposit_due_day;
            const today = new Date();
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
            const effectiveDueDay = Math.min(dueDay, lastDayOfMonth);
            const dueDate = new Date(currentYear, currentMonth, effectiveDueDay);
            const isToday = today.getDate() === effectiveDueDay;
            const isPast = today > dueDate;

            // Check if there's a deposit this month
            const hasDepositThisMonth = history.some(h => {
              const d = new Date(h.created_at);
              return h.type === 'deposit' && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
            });

            const statusIcon = hasDepositThisMonth ? <CheckCircle size={18} /> : isPast ? <AlertTriangle size={18} /> : <Calendar size={18} />;
            const statusColor = hasDepositThisMonth ? '#22c55e' : isPast ? '#ef4444' : '#f59e0b';
            const statusBg = hasDepositThisMonth ? 'rgba(34,197,94,0.06)' : isPast ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)';
            const statusBorder = hasDepositThisMonth ? 'rgba(34,197,94,0.15)' : isPast ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)';
            const statusText = hasDepositThisMonth
              ? 'Depósito deste mês realizado!'
              : isPast
                ? `Depósito atrasado! Venceu dia ${effectiveDueDay.toString().padStart(2,'0')}/${(currentMonth+1).toString().padStart(2,'0')}`
                : isToday
                  ? 'Hoje é o dia do seu depósito!'
                  : `Próximo depósito: dia ${effectiveDueDay.toString().padStart(2,'0')}/${(currentMonth+1).toString().padStart(2,'0')}`;

            const daysLeft = !hasDepositThisMonth && !isPast ? Math.ceil((dueDate - today) / (1000*60*60*24)) : null;

            return (
              <div className="due-day-banner" style={{ background: statusBg, border: `1px solid ${statusBorder}` }}>
                <div className="due-day-icon" style={{ color: statusColor }}>
                  {statusIcon}
                </div>
                <div className="due-day-info">
                  <span className="due-day-text" style={{ color: statusColor }}>{statusText}</span>
                  {daysLeft !== null && daysLeft > 0 && (
                    <span className="due-day-sub">Faltam {daysLeft} dia{daysLeft !== 1 ? 's' : ''}</span>
                  )}
                </div>
                {isToday && !hasDepositThisMonth && (
                  <span className="due-day-today-tag">HOJE</span>
                )}
              </div>
            );
          })()}

          {/* Loans Section */}
          {loans.length > 0 && (
            <div className="history-card" style={{ marginBottom: 16 }}>
              <div className="history-card-header">
                <h3 className="section-title"><Banknote size={17} />Meus Empréstimos</h3>
                <span className="history-count">{loans.filter(l => l.status === 'ativo' || l.status === 'atrasado').length} ativo{loans.filter(l => l.status === 'ativo' || l.status === 'atrasado').length !== 1 ? 's' : ''}</span>
              </div>
              <div className="history-list">
                {loans.map((loan) => {
                  const paidCount = loan.installments ? loan.installments.filter(i => i.status === 'pago').length : 0;
                  const totalCount = loan.installments ? loan.installments.length : 0;
                  const loanProg = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
                  const nextInst = loan.installments ? loan.installments.find(i => i.status !== 'pago') : null;
                  const statusColor = loan.status === 'pago' ? '#22c55e' : loan.status === 'atrasado' ? '#ef4444' : '#8b5cf6';
                  const statusLabel = loan.status === 'pago' ? 'Quitado' : loan.status === 'atrasado' ? 'Atrasado' : loan.status === 'cancelado' ? 'Cancelado' : 'Ativo';

                  return (
                    <div key={loan.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${statusColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Banknote size={16} style={{ color: statusColor }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(loan.total_amount)}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {loan.description || 'Empréstimo'} · {paidCount}/{totalCount} parcelas
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: `${statusColor}15`, color: statusColor, fontWeight: 600 }}>
                          {statusLabel}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--progress-bg)' }}>
                          <div style={{ width: `${loanProg}%`, height: '100%', borderRadius: 3, background: statusColor, transition: 'width 0.5s ease' }} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 32 }}>{loanProg.toFixed(0)}%</span>
                      </div>

                      {nextInst && loan.status !== 'pago' && loan.status !== 'cancelado' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                          <Clock size={12} />
                          <span>Próxima parcela: <strong>{formatCurrency(nextInst.amount)}</strong> em <strong>{new Date(nextInst.due_date + 'T12:00:00').toLocaleDateString('pt-BR')}</strong></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="history-card">
            <div className="history-card-header">
              <h3 className="section-title"><History size={17} />Histórico de transações</h3>
              <span className="history-count">{history.length} registro{history.length !== 1 && 's'}</span>
            </div>
            {history.length === 0 ? (
              <div className="empty-history"><History size={32} className="empty-icon" /><p>Nenhuma transação realizada</p></div>
            ) : (
              <div className="history-list">
                {history.map((item) => (
                  <div key={item.id} className="history-item">
                    <div className={`history-icon ${item.type}`}>
                      {item.type === 'deposit' ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                    </div>
                    <div className="history-info">
                      <span className="history-desc">{item.description || item.type}</span>
                      <span className="history-date">{formatDate(item.created_at)} às {formatTime(item.created_at)}</span>
                    </div>
                    <span className={`history-amount ${item.type}`}>
                      {item.type === 'deposit' ? '+' : '-'} {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {modal === 'deposit' && (
        <div className="modal-overlay" onClick={() => { if (!pixData) { setModal(null); setError(''); } }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" />
            <div className="modal-header">
              <div>
                <h3>{pixData ? 'Pague o PIX' : 'Depositar via PIX'}</h3>
                <p className="modal-subtitle">{pixData ? 'Escaneie o QR Code ou copie o código PIX' : 'Informe o valor para gerar o PIX'}</p>
              </div>
              <button className="modal-close" onClick={() => { setModal(null); setError(''); setPixData(null); setPixPolling(false); }}><X size={18} /></button>
            </div>

            {!pixData ? (
              <form onSubmit={handleTransaction} className="modal-form">
                <div className="modal-input-group">
                  <label>Valor</label>
                  <div className="modal-input-wrap">
                    <span className="modal-input-prefix">R$</span>
                    <input type="number" min="0.01" step="0.01" placeholder="0,00" value={amount}
                      onChange={(e) => setAmount(e.target.value)} autoFocus required />
                  </div>
                </div>
                {error && <div className="modal-error"><AlertTriangle size={14} /><span>{error}</span></div>}
                <button type="submit" className="modal-submit deposit" disabled={loading}>
                  {loading ? <div className="spinner-sm" /> : <><QrCode size={17} /> Gerar PIX</>}
                </button>
              </form>
            ) : (
              <div className="modal-form">
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--title-color)', marginBottom: 4 }}>
                    {formatCurrency(pixData.amount)}
                  </div>

                  {pixData.qrCodeImage ? (
                    <div style={{ margin: '16px auto', background: '#ffffff', padding: 16, borderRadius: 12, display: 'inline-block', border: '2px solid #e5e7eb' }}>
                      <img
                        src={pixData.qrCodeImage}
                        alt="QR Code PIX"
                        style={{ width: 256, height: 256, display: 'block', imageRendering: 'pixelated', background: '#ffffff' }}
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="width:200px;height:200px;display:flex;align-items:center;justify-content:center;color:#ef4444;font-size:13px;text-align:center">Erro ao carregar QR Code. Use o código PIX abaixo.</div>'; }}
                      />
                    </div>
                  ) : !pixData.emvCode ? (
                    <div style={{ margin: '16px 0', padding: '20px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, color: '#ef4444', fontSize: 13 }}>
                      <AlertTriangle size={16} style={{ marginBottom: 4 }} />
                      <div style={{ marginTop: 4 }}>Não foi possível gerar o QR Code. Tente novamente em alguns instantes.</div>
                    </div>
                  ) : null}

                  {pixData.emvCode && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{
                        background: 'var(--input-bg, rgba(0,0,0,0.05))', border: '1px solid var(--border)',
                        borderRadius: 8, padding: '10px 12px', fontSize: 11, wordBreak: 'break-all',
                        color: 'var(--text-muted)', maxHeight: 60, overflow: 'auto', marginBottom: 8
                      }}>
                        {pixData.emvCode}
                      </div>
                      <button type="button" onClick={copyEmvCode}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                          background: pixCopied ? '#22c55e' : '#3b82f6', color: '#fff', border: 'none',
                          borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif'
                        }}>
                        {pixCopied ? <><Check size={14} /> Copiado!</> : <><Copy size={14} /> Copiar código PIX</>}
                      </button>
                    </div>
                  )}

                  {pixPolling && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16, color: '#f59e0b', fontSize: 13 }}>
                      <Loader size={14} className="spin-animation" />
                      <span>Aguardando pagamento...</span>
                    </div>
                  )}
                </div>

                <button type="button" className="modal-submit" style={{ background: '#6b7280', marginTop: 8 }}
                  onClick={() => { setPixData(null); setPixPolling(false); setModal(null); setError(''); setAmount(''); }}>
                  <X size={17} /> Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {modal === 'withdraw' && (
        <div className="modal-overlay" onClick={() => { setModal(null); setError(''); }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line withdraw" />
            <div className="modal-header">
              <div><h3>Sacar via PIX</h3><p className="modal-subtitle">O valor será enviado para a chave PIX do seu CPF</p></div>
              <button className="modal-close" onClick={() => { setModal(null); setError(''); }}><X size={18} /></button>
            </div>
            <div className="withdraw-rules">
              <div className="rule-item">
                <div className="rule-icon-wrap"><Calendar size={15} /></div>
                <div className="rule-text">
                  <span className="rule-title">{isDailyMode ? '1 saque a cada 24h' : '1 saque por mês'}</span>
                  <span className="rule-desc">{hasWithdrawnRecently ? (isDailyMode ? 'Saque já realizado nas últimas 24h' : 'Saque já realizado este mês') : (isDailyMode ? 'Saque disponível agora' : 'Saque disponível este mês')}</span>
                </div>
                <div className={`rule-status ${hasWithdrawnRecently ? 'used' : 'available'}`}>
                  {hasWithdrawnRecently ? 'Usado' : 'Disponível'}
                </div>
              </div>
              <div className="rule-item">
                <div className="rule-icon-wrap"><Percent size={15} /></div>
                <div className="rule-text">
                  <span className="rule-title">Limite de 20%</span>
                  <span className="rule-desc">Máximo: {formatCurrency(maxWithdraw)}</span>
                </div>
              </div>
            </div>
            <form onSubmit={handleTransaction} className="modal-form">
              <div className="modal-input-group">
                <label>Valor</label>
                <div className="modal-input-wrap">
                  <span className="modal-input-prefix">R$</span>
                  <input type="number" min="0.01" step="0.01" max={maxWithdraw} placeholder="0,00" value={amount}
                    onChange={(e) => setAmount(e.target.value)} autoFocus required disabled={hasWithdrawnRecently} />
                </div>
              </div>
              <div className="modal-balance-info">Saldo disponível: <strong>{formatCurrency(balance)}</strong></div>
              {(withdrawError || error) && (
                <div className="modal-error"><AlertTriangle size={14} /><span>{withdrawError || error}</span></div>
              )}
              <button type="submit" className="modal-submit withdraw"
                disabled={loading || hasWithdrawnRecently || !amount || parseFloat(amount) > maxWithdraw || parseFloat(amount) > balance}>
                {loading ? <div className="spinner-sm" /> : <><Minus size={17} /> Retirar</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
